import {
  Button,
  Box,
  createStyles,
  Text,
  Select,
  TextInput,
  Modal,
  Loader,
} from "@mantine/core";
import swal from "sweetalert";
import { useForm } from "@mantine/form";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { createpayer, editPayer, getpayer } from "../../services/payer";
import { stateZipCodes } from "../../utils/zipCodes";
import { searchAddress, getAddressByPlaceId } from "../../services/dashboard";

// Design tokens mirrored from the billing redesign spec
const T = {
  surface: "var(--app-surface)",
  surfaceAlt: "var(--app-surface-2)",
  line: "var(--app-border)",
  lineSoft: "var(--app-border)",
  ink: "var(--app-text)",
  ink2: "var(--app-text)",
  muted: "var(--app-text-muted)",
  accent: "var(--app-primary)",
  accentHover: "var(--app-primary)",
  danger: "#DC2626",
};

const useStyles = createStyles(() => ({
  modalContent: {
    borderRadius: 12,
    overflow: "hidden",
  },
  modalBody: {
    padding: 0,
  },
  head: {
    padding: "22px 28px",
    borderBottom: `1px solid ${T.lineSoft}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  eyebrow: {
    margin: "0 0 6px",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: T.muted,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: T.ink,
    letterSpacing: "-0.018em",
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: T.muted,
  },
  closeBtn: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: 8,
    background: T.surfaceAlt,
    border: 0,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    color: T.ink2,
    flexShrink: 0,
    "&:hover": { background: "var(--app-surface-2)" },
    "& svg": { width: 18, height: 18 },
  },
  body: {
    padding: "22px 28px",
    overflowY: "auto",
    maxHeight: "calc(92vh - 226px)",
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: 16,
    fontWeight: 600,
    color: T.ink,
  },
  sectionTitleSpaced: {
    marginTop: 22,
  },
  grid: {
    display: "grid",
    gap: "16px 18px",
  },
  fieldLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: T.ink2,
    marginBottom: 6,
  },
  req: {
    color: T.danger,
  },
  foot: {
    padding: "8px 23px 21px",
    borderTop: `1px solid ${T.lineSoft}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
  },
  ghostBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    background: "transparent",
    color: T.ink2,
    border: `1px solid ${T.line}`,
    "&:hover": { background: T.surfaceAlt },
  },
  accentBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    background: T.accent,
    color: T.ink,
    border: "1px solid transparent",
    "&:hover": { background: T.accentHover },
  },
}));

const inputStyles = {
  input: {
    height: 42,
    minHeight: 42,
    padding: "0 12px",
    border: `1px solid ${T.line}`,
    background: T.surface,
    borderRadius: 8,
    fontSize: 13,
    color: T.ink,
    "&:focus": {
      borderColor: T.ink,
      boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
    },
  },
};

const Field = ({ classes, label, required, children }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <Text className={classes.fieldLabel}>
      {label} {required && <span className={classes.req}>*</span>}
    </Text>
    {children}
  </div>
);

const FIELD_LABELS = {
  payerName: "Payer Name",
  payerId: "Payer ID",
  stediPayerId: "ClearingHouse Payer ID",
  payerEntity: "Payer Entity",
  payerAddress1: "Address 1",
  payerAddress2: "Address 2",
  payerCity: "City",
  payerState: "State",
  payerZip: "Zip",
};

// isEdit mode: opened via EditPayerModalOpen/setEditPayerModalOpen + item + isPreview
// add mode:    opened via state.addPayer / setState
function AddPayer({
  // add mode
  state,
  setState,
  payerclient,
  // edit mode
  EditPayerModalOpen,
  setEditPayerModalOpen,
  item,
  isPreview,
  onSaved,
}) {
  const { classes, cx } = useStyles();
  const { profile } = useSelector((s) => {
    return s?.profile;
  });
  
 const isEdit = EditPayerModalOpen !== undefined;
  const opened = isEdit ? EditPayerModalOpen : state?.addPayer;
  const showStediPayerId = isEdit && Boolean(item?.stediPayerId);

  const handleClose = () => {
    if (isEdit) {
      setEditPayerModalOpen(false);
    } else {
      setState((prev) => ({ ...prev, addPayer: false }));
    }
  };

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);
  const [addr2Suggestions, setAddr2Suggestions] = useState([]);
  const [showSuggestions2, setShowSuggestions2] = useState(false);
  const [addr2Loading, setAddr2Loading] = useState(false);
  const debounceRef2 = useRef(null);

  const form = useForm({
    initialValues: {
      payerName: "",
      payerId: "",
      stediPayerId: "",
      payerEntity: "",
      transaction: "",
      payerAddress1: "",
      payerAddress2: "",
      payerCity: "",
      payerState: "",
      payerZip: "",
    },
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (isEdit && item) {
      form.setValues({
        payerName: item?.payerName || "",
        payerId: item?.payerId || "",
        stediPayerId: item?.stediPayerId || "",
        payerEntity: item?.payerEntity || "",
        transaction: item?.transaction || "",
        payerAddress1: item?.payerAddress1 || "",
        payerAddress2: item?.payerAddress2 || "",
        payerCity: item?.payerCity || "",
        payerState: item?.payerState || "",
        payerZip: String(item?.payerZip || ""),
      });
    }
    if (!opened) {
      form.reset();
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setAddr2Suggestions([]);
      setShowSuggestions2(false);
    }
  }, [item, opened]);

  const handleAddressSearch = (value) => {
    form.setFieldValue("payerAddress1", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchAddress(value);
        setAddressSuggestions(results || []);
        setShowSuggestions(true);
      } catch (e) {
        setAddressSuggestions([]);
      }
      setAddressLoading(false);
    }, 400);
  };

  const handleAddress2Search = (value) => {
    form.setFieldValue("payerAddress2", value);
    if (debounceRef2.current) clearTimeout(debounceRef2.current);
    if (!value || value.length < 3) { setAddr2Suggestions([]); setShowSuggestions2(false); return; }
    debounceRef2.current = setTimeout(async () => {
      setAddr2Loading(true);
      try {
        const results = await searchAddress(value);
        setAddr2Suggestions(results || []);
        setShowSuggestions2(true);
      } catch (e) { setAddr2Suggestions([]); }
      setAddr2Loading(false);
    }, 400);
  };

  const handleSelectAddress2 = (suggestion) => {
    form.setFieldValue("payerAddress2", suggestion.formatted_address);
    setShowSuggestions2(false);
  };

  const handleSelectAddress = async (suggestion) => {
    setShowSuggestions(false);
    setAddressLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components = placeDetails?.result?.address_components || [];
      let streetNumber = "", route = "", city = "", addrState = "", zipcode = "";
      components.forEach((comp) => {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1")) addrState = comp.short_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      });
      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      form.setFieldValue("payerAddress1", streetAddress || suggestion.formatted_address);
      if (city) form.setFieldValue("payerCity", city);
      if (addrState) form.setFieldValue("payerState", addrState);
      if (zipcode) form.setFieldValue("payerZip", zipcode);
    } catch (e) {
      form.setFieldValue("payerAddress1", suggestion.formatted_address);
    }
    setAddressLoading(false);
  };

  const onSave = async () => {
    const values = form.values;

    const optionalFields = [
      "payerAddress1",
      "payerAddress2",
      "payerEntity",
      "payerCity",
      "payerState",
      "payerZip",
      "stediPayerId",
      "transaction",
    ];
    const missingFields = Object.entries(values)
      .filter(([key]) => !optionalFields.includes(key))
      .filter(([, value]) => !value)
      .map(([key]) => FIELD_LABELS[key] || key);

    if (missingFields.length > 0) {
      await swal({
        title: "Please Fill Out The Following Fields",
        text: missingFields.join(", "),
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    const selectedState = values.payerState?.trim();
    const enteredZipCode = values.payerZip?.trim();

    if (selectedState && enteredZipCode) {
      const stateZipCodeEntries = stateZipCodes.filter(
        (entry) => entry.ste_name === selectedState
      );
      const isZipValid = stateZipCodeEntries.some(
        (entry) => entry.zip_code === enteredZipCode
      );
      if (!isZipValid) {
        await swal({
          title: "Invalid Zip Code",
          text: `The zip code you entered is not valid for ${selectedState}. Please enter a valid zip code.`,
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
    }

    if (isEdit) {
      const payload = {
        values,
        createdBy: profile.user.id,
        // admin: profile?.user?.admin ?? profile?.user?.id,
        admin:(profile?.user?.admin && profile?.user?.role) ? profile?.user?.admin : profile?.user?.id,
        id: item.id,
      };
      const res = await editPayer(payload);
      if (res.status === 200) {
        getpayer({ page: 1 });
        onSaved?.();
        handleClose();
        swal({
          title: "Payer updated successfully",
          text: "Payer update successful",
          dangerMode: false,
        });
      }
    } else {
      const { stediPayerId, ...createValues } = values;
      const payload = {
        values: createValues,
        createdBy: profile.user.id,
        // admin: profile?.user?.admin ?? profile?.user?.id,
        admin:(profile?.user?.admin && profile?.user?.role) ? profile?.user?.admin : profile?.user?.id,
      };
      const res = await createpayer(payload);
      if (res.status === 200) {
        getpayer({ page: 1 });
        onSaved?.();
        swal({
          title: "Payer added successfully",
          text: "Payer added successfully",
          dangerMode: false,
        });
        form.reset();
        handleClose();
      }
    }
  };

  const stateData = Array.from(
    new Set(stateZipCodes?.map((obj) => obj.ste_name))
  )
    ?.sort()
    ?.map((ste_name) => ({ value: ste_name, label: ste_name }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      size={560}
      radius={12}
      padding={0}
      zIndex={1000}
      centered
      classNames={{ content: classes.modalContent, body: classes.modalBody }}
      overlayProps={{ opacity: 0.55, color: "var(--app-text)" }}
    >
      {/* ── Head ── */}
      <Box className={classes.head}>
        <div>
          <Text className={classes.eyebrow}>
            {isEdit ? "Edit Payer" : "Custom Payer"}
          </Text>
          <Text component="h2" className={classes.title}>
            {isEdit ? "Edit payer" : "Add custom payer"}
          </Text>
          <Text className={classes.subtitle}>
            {isEdit
              ? "Update the payer information below."
              : "Add a payer that isn't in the master list. You'll fill out the standard payer info, and your billing team will verify enrollment details before submitting claims."}
          </Text>
        </div>
        <button
          type="button"
          className={classes.closeBtn}
          onClick={handleClose}
          aria-label="Close"
        >
          <IconX />
        </button>
      </Box>

      {/* ── Body ── */}
      <Box className={classes.body}>
        <form>
          <Text component="h6" className={classes.sectionTitle}>
            Payer information
          </Text>

          <Box className={classes.grid} style={{ gridTemplateColumns: "1fr" }}>
            <Field classes={classes} label="Payer name" required>
              <TextInput
                placeholder="e.g. Aetna PPO of Florida"
                styles={inputStyles}
                disabled={isPreview}
                {...form.getInputProps("payerName")}
              />
            </Field>
          </Box>

          <Box
            className={classes.grid}
            style={{
              gridTemplateColumns: showStediPayerId
                ? "repeat(2, minmax(0, 1fr))"
                : "1fr",
              marginTop: 14,
            }}
          >
            <Field classes={classes} label="Payer ID" required>
              <TextInput
                placeholder="e.g. 38692"
                styles={inputStyles}
                disabled={isPreview}
                {...form.getInputProps("payerId")}
              />
            </Field>
            {showStediPayerId && (
              <Field classes={classes} label="ClearingHouse Payer ID">
                <TextInput
                  placeholder="e.g. 00630"
                  styles={inputStyles}
                  disabled={isPreview}
                  {...form.getInputProps("stediPayerId")}
                />
              </Field>
            )}
          </Box>

          <Box
            className={classes.grid}
            style={{ gridTemplateColumns: "1fr", marginTop: 14 }}
          >
            <Field classes={classes} label="Payer Entity">
              <Select
                placeholder="Select Payer"
                styles={inputStyles}
                rightSection={<IconChevronDown color={"var(--app-text-muted)"} size={20} />}
                disabled={isPreview}
                data={[
                  { value: "Medicare", label: "Medicare" },
                  { value: "Medicaid", label: "Medicaid" },
                  { value: "Tricare", label: "Tricare" },
                  { value: "ChampVA", label: "ChampVA" },
                  {
                    value: "Group Health Insurance",
                    label: "Group Health Insurance",
                  },
                  { value: "FEKA BLK LUNG", label: "FEKA BLK LUNG" },
                  { value: "Other", label: "Other" },
                ]}
                {...form.getInputProps("payerEntity")}
              />
            </Field>
          </Box>

          <Text
            component="h6"
            className={cx(classes.sectionTitle, classes.sectionTitleSpaced)}
          >
            Address (optional)
          </Text>

          <Box className={classes.grid} style={{ gridTemplateColumns: "1fr" }}>
            <Field classes={classes} label="Address 1">
              <Box style={{ position: "relative" }}>
                <TextInput
                  placeholder="123 Main St"
                  styles={inputStyles}
                  disabled={isPreview}
                  value={form.values.payerAddress1}
                  onChange={(e) => handleAddressSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                  rightSection={addressLoading ? <Loader size="xs" /> : null}
                />
                {showSuggestions && addressSuggestions.length > 0 && (
                  <Box
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1200,
                      background: "var(--app-surface)",
                      border: "1px solid var(--app-border)",
                      borderRadius: 8,
                      maxHeight: 200,
                      overflowY: "auto",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  >
                    {addressSuggestions.map((s, i) => (
                      <Box
                        key={s.place_id || i}
                        style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14 }}
                        sx={{ "&:hover": { background: "var(--app-surface-2)" } }}
                        onMouseDown={() => handleSelectAddress(s)}
                      >
                        {s.formatted_address}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Field>
            <Field classes={classes} label="Address 2">
              <Box style={{ position: "relative" }}>
                <TextInput
                  styles={inputStyles}
                  disabled={isPreview}
                  value={form.values.payerAddress2}
                  onChange={(e) => handleAddress2Search(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSuggestions2(false), 200)}
                  onFocus={() => addr2Suggestions.length > 0 && setShowSuggestions2(true)}
                  rightSection={addr2Loading ? <Loader size="xs" /> : null}
                />
                {showSuggestions2 && addr2Suggestions.length > 0 && (
                  <Box style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1200, background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 8, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                    {addr2Suggestions.map((s, i) => (
                      <Box key={s.place_id || i} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14 }} sx={{ "&:hover": { background: "var(--app-surface-2)" } }} onMouseDown={() => handleSelectAddress2(s)}>
                        {s.formatted_address}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Field>
          </Box>

          <Box
            className={classes.grid}
            style={{
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              marginTop: 14,
            }}
          >
            <Field classes={classes} label="City">
              <TextInput
                styles={inputStyles}
                disabled={isPreview}
                {...form.getInputProps("payerCity")}
              />
            </Field>
            <Field classes={classes} label="State">
              <Select
                searchable
                data={stateData}
                styles={inputStyles}
                rightSection={<IconChevronDown color={"var(--app-text-muted)"} size={20} />}
                disabled={isPreview}
                {...form.getInputProps("payerState")}
              />
            </Field>
            <Field classes={classes} label="Zip">
              <TextInput
                type="number"
                styles={inputStyles}
                disabled={isPreview}
                {...form.getInputProps("payerZip")}
              />
            </Field>
          </Box>
        </form>
      </Box>

      {/* ── Foot ── */}
      <Box className={classes.foot}>
        <Button className={classes.ghostBtn} onClick={handleClose}>
          Cancel
        </Button>
        {!isPreview && (
          <Button className={classes.accentBtn} onClick={onSave}>
            {isEdit ? "Save changes" : "Add payer"}
          </Button>
        )}
      </Box>
    </Modal>
  );
}

export default AddPayer;
